'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { CateringForm } from '@/components/CateringForm';
import { ChefHat } from 'lucide-react';

export default function CateringPage() {
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="w-full" style={{ backgroundColor: 'hsl(35 100% 97%)' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="section-cream texture-cream py-20 sm:py-24 px-4">
        <div className="container mx-auto text-center max-w-3xl">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-[#D71920] mb-4 sm:mb-6 uppercase tracking-tight">
            Bring Mo's Burger Truck to Your Event
          </h1>
          <p className="text-lg sm:text-xl text-foreground mb-8 sm:mb-10 font-medium">
            Fresh halal smash burgers served hot and made-to-order for your guests. Perfect for corporate events, private parties, weddings, schools, festivals, and community gatherings.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-[#D71920] hover:bg-[#B01017] text-white font-black text-base sm:text-lg py-4 sm:py-5 px-8 sm:px-12 rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2"
          >
            <ChefHat className="h-5 w-5 sm:h-6 sm:w-6" />
            Submit Catering Inquiry
          </Button>
        </div>
      </section>

      {/* Event Types Section */}
      <section className="py-12 sm:py-16 section-beige px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-[#333] mb-3 sm:mb-4 uppercase tracking-wide">
              Perfect For
            </h2>
            <p className="text-base sm:text-lg text-foreground leading-relaxed font-medium">
              Private parties · Corporate lunches · Weddings · School events · Festivals · Community gatherings
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Packages Section */}
      <section className="py-16 sm:py-20 section-cream texture-cream px-4">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-black text-[#D71920] mb-3 sm:mb-4 uppercase tracking-tight">
                Pricing Packages
              </h2>
              <p className="text-base sm:text-lg text-foreground font-medium">
                Pricing per person plus transportation fees
              </p>
            </div>

            {/* Package Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
              {/* Basic Package */}
              <div className="bg-white rounded-2xl sm:rounded-3xl border border-border p-6 sm:p-8 hover:shadow-md transition-all">
                <h3 className="text-2xl sm:text-3xl font-black text-[#D71920] mb-4 uppercase tracking-tight">
                  Basic Package
                </h3>
                <div className="mb-6">
                  <p className="text-4xl sm:text-5xl font-black text-foreground">$16</p>
                  <p className="text-sm text-foreground/60 font-semibold">per person</p>
                </div>
                <ul className="space-y-3 text-sm sm:text-base text-foreground/80 font-medium mb-8">
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> Smash burgers
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> French fries
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> Beverages
                  </li>
                </ul>
              </div>

              {/* Deluxe Package - Featured */}
              <div className="bg-[#D71920] rounded-2xl sm:rounded-3xl border-2 border-[#D71920] p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all relative -scale-y-[1.05] md:scale-y-100">
                <div className="absolute top-4 right-4 bg-black text-white px-3 py-1 rounded-full text-xs font-black uppercase">
                  Most Popular
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-4 uppercase tracking-tight">
                  Deluxe Package
                </h3>
                <div className="mb-6">
                  <p className="text-4xl sm:text-5xl font-black text-white">$21</p>
                  <p className="text-sm text-white/80 font-semibold">per person</p>
                </div>
                <ul className="space-y-3 text-sm sm:text-base text-white font-medium mb-8">
                  <li className="flex items-center gap-2">
                    <span className="text-white font-black">✓</span> Smash burgers
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-white font-black">✓</span> Crazy fries & sides
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-white font-black">✓</span> Beverages
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-white font-black">✓</span> Sauce packets & extras
                  </li>
                </ul>
              </div>

              {/* Premium Package */}
              <div className="bg-white rounded-2xl sm:rounded-3xl border border-border p-6 sm:p-8 hover:shadow-md transition-all">
                <h3 className="text-2xl sm:text-3xl font-black text-[#D71920] mb-4 uppercase tracking-tight">
                  Premium Package
                </h3>
                <div className="mb-6">
                  <p className="text-4xl sm:text-5xl font-black text-foreground">$27</p>
                  <p className="text-sm text-foreground/60 font-semibold">per person</p>
                </div>
                <ul className="space-y-3 text-sm sm:text-base text-foreground/80 font-medium mb-8">
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> Premium burgers
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> Full menu selections
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> All beverages
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#D71920] font-black">✓</span> Customization & extras
                  </li>
                </ul>
              </div>
            </div>

            {/* Additional Fees */}
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-border p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-black text-foreground mb-6 uppercase tracking-tight">
                Additional Fees
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-border">
                  <span className="font-semibold text-foreground">Transportation Fee</span>
                  <span className="font-black text-[#D71920] text-lg">$175</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-foreground">Distance Charge (after 10-mile radius)</span>
                  <span className="font-black text-[#D71920] text-lg">$2 per mile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catering Images Section */}
      <section className="section-cream texture-cream py-16 sm:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10">
            {/* Food Truck Image */}
            <div className="relative w-full aspect-square rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-100 border-2 border-[#D71920]/20 shadow-lg hover:shadow-xl transition-all">
              <Image
                src="/images/uploads/7496e30b-32f5-44b2-a5b0-ac04afa6c1cf.jpg"
                alt="Mo's Burger Food Truck"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>

            {/* Catering Image */}
            <div className="relative w-full aspect-square rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-100 border-2 border-[#D71920]/20 shadow-lg hover:shadow-xl transition-all">
              <Image
                src="/images/uploads/2089c033-1b44-408c-b206-759d078bbac7.png"
                alt="Mo's Burger Catering Setup"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Catering Form Section */}
      <section ref={formRef} className="py-16 sm:py-20 section-cream texture-cream px-4">
        <div className="container mx-auto">
          <div className="mb-10 sm:mb-12 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-black text-[#333] mb-3 uppercase tracking-tight">
              Event Inquiry
            </h2>
            <p className="text-foreground text-base sm:text-lg font-medium">
              Tell us about your event. We'll follow up with availability and pricing.
            </p>
            <p className="text-foreground text-sm sm:text-base mt-3">
              Inquiries sent to{' '}
              <a href="mailto:Catering@mosburgerdc.com" className="font-bold text-[#D71920] hover:text-[#B01017]">
                Catering@mosburgerdc.com
              </a>
            </p>
          </div>
          <CateringForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
